import { Tooltip } from "@mui/material";
import { CustomTable, Map, TitleWithRightAction } from "components";
import { useMemo, useState } from "react";
import { formatNumber } from "utils/formatNumber";

const MapSection = ({ data }) => {
  // Detail Table Cells
  const detailHeadCells = [
    {
      id: "group",
      label: <span className='text-[10px] text-[#5F737F]'>Region</span>,
      width: 10,
    },
    {
      id: "no_of_shareholders",
      label: (
        <span className='text-[10px] text-[#5F737F]'>
          No. of shareholders (%)
        </span>
      ),
      numeric: true,
      width: 50,
    },
    {
      id: "ownership_percentage",
      label: (
        <span className='text-[10px] text-[#5F737F]'>Shares ownership</span>
      ),
      numeric: true,
      width: 50,
    },
  ];
  const detailBodyCells = useMemo(
    () =>
      data?.distribution?.map((el) => ({
        ...el,
        id: el?.group,
        group: <span className='text-xs text-[#5F737F]'>{el?.group}</span>,
        ownership_percentage: (
          <span className='text-xs'>{`${(
            el?.ownership_percentage ?? 0
          )?.toFixed(2)}%`}</span>
        ),
        no_of_shareholders: (
          <span className='text-xs'>
            {`${formatNumber.toAmount(
              el?.no_of_shareholders,
            )} (${el?.shareholder_percentage?.toFixed(2)}%)`}
          </span>
        ),
      })),
    [data?.distribution],
  );

  // Top Region Table Cells
  const topRegionHeadCells = [
    {
      id: "group",
      label: <span className='text-sm text-[#5F737F]'>Top Region</span>,
      width: 10,
    },
    {
      id: "no_of_shareholders",
      label: (
        <span className='text-sm text-[#5F737F]'>No. of shareholders (%)</span>
      ),
      numeric: true,
      width: 50,
    },
    {
      id: "ownership_percentage",
      label: <span className='text-sm text-[#5F737F]'>Shares ownership</span>,
      numeric: true,
      width: 50,
    },
  ];
  const topRegionBodyCells = useMemo(() => {
    const maxDatum = [
      data?.distribution?.[0],
      data?.distribution?.[1],
      data?.distribution?.[2],
      data?.distribution?.[3],
    ];
    const result = maxDatum?.filter((el) => Boolean(el?.ownership_percentage));
    return result?.map((el) => ({
      ...el,
      id: el?.group,
      shareholderNum: el?.ownership_percentage,
      group: <span className='text-sm text-[#5F737F]'>{el?.group}</span>,
      ownership_percentage: (
        <span className='text-sm'>{`${(el?.ownership_percentage ?? 0)?.toFixed(
          2,
        )}%`}</span>
      ),
      no_of_shareholders: (
        <span className='text-sm'>
          {`${formatNumber.toAmount(
            el?.no_of_shareholders,
          )} (${el?.shareholder_percentage?.toFixed(2)}%)`}
        </span>
      ),
    }));
  }, [data?.distribution]);
  const [isDetail, setIsDetail] = useState(false);

  const foreignRegion = useMemo(() => {
    const foundForeign = topRegionBodyCells?.find(
      (el) => el?.id === "Foreign (outside Indonesia)",
    );
    const shareholderNumPercentage = foundForeign?.shareholderNum;

    let color = "bg-[#0022ff]";
    if (shareholderNumPercentage > 0 && shareholderNumPercentage <= 10) {
      color = "bg-[#ecefff]";
    }
    if (shareholderNumPercentage > 10 && shareholderNumPercentage <= 25) {
      color = "bg-[#d1d8fc]";
    }
    if (shareholderNumPercentage > 25 && shareholderNumPercentage <= 50) {
      color = "bg-[#96a7fb]";
    }
    if (shareholderNumPercentage > 50 && shareholderNumPercentage <= 75) {
      color = "bg-[#637bf6]";
    }

    return {
      color,
      el: foundForeign,
    };
  }, [topRegionBodyCells]);

  return (
    <div className='section map-section h-full'>
      <TitleWithRightAction
        title='Shareholders by Region'
        tooltipText='Regions where investors are from'
      >
        <span
          onClick={() => setIsDetail((prev) => !prev)}
          className='cursor-pointer text-blue-700 font-bold hover:text-blue-500 text-xs'
        >
          {isDetail ? "Close" : "View Details"}
        </span>
      </TitleWithRightAction>

      <div className='mt-5'>
        {isDetail ? (
          // Detail
          <CustomTable
            id='all-region-table'
            maxHeight={480}
            headCells={detailHeadCells}
            bodyCells={detailBodyCells}
            showPagination={false}
            borderless
            sx={{
              ".MuiTableCell-root.MuiTableCell-head": {
                padding: "0px !important",
              },
              ".MuiTableCell-root.MuiTableCell-body": {
                px: "0px !important",
                py: "5px !important",
              },
              ".MuiTableHead-root": {
                background: "transparent",
              },
              ".MuiTableCell-root": {
                borderBottom: "none",
              },
            }}
          />
        ) : (
          // Map Section + Top Region Shareholder
          <div>
            <div className='w-full border pb-3'>
              {Boolean(foreignRegion?.el) && (
                <Tooltip
                  title={
                    <div className='my-1'>
                      <div
                        className={`rounded-full w-[6px] h-[6px] mb-1 border ${foreignRegion?.color}`}
                      />
                      <span className='text-[14px]'><span className='font-bold'>{foreignRegion?.el?.id}</span>: {foreignRegion?.el?.shareholderNum}%</span>
                    </div>
                  }
                >
                  <div
                    className={`border cursor-pointer w-[75px] h-[35px] flex justify-center items-center align-middle hover:bg-[#BADA55] hover:border-gray-400 hover:border-2 ${foreignRegion?.color}`}
                  >
                    <span className='text-[14px] font-bold align-middle'>
                      Foreign
                    </span>
                  </div>
                </Tooltip>
              )}
              <Map
                data={topRegionBodyCells?.map((el) => [
                  el?.add_on,
                  el?.shareholderNum,
                ])}
              />
            </div>
            <CustomTable
              id='top-region-table'
              headCells={topRegionHeadCells}
              bodyCells={topRegionBodyCells}
              showPagination={false}
              borderless
              tableContainerStyle={{ paddingBottom: "20px" }}
              sx={{
                ".MuiTableCell-root.MuiTableCell-head": {
                  px: "0px !important",
                  py: "10px !important",
                },
                ".MuiTableCell-root.MuiTableCell-body": {
                  px: "0px !important",
                  py: "0px !important",
                  height: "100%",
                  borderBottom: "none",
                },
                ".MuiTableHead-root": {
                  background: "transparent",
                },
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default MapSection;
